/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import firebase from "firebase/app";
import { useI18n } from "hooks/i18n/i18n";
import { useState } from "react";

import { formatPhoneNumber } from "./PhoneNumberField";
import { PhoneNumberVerificationForm } from "./PhoneNumberVerificationForm";

import "firebase/auth";

import type { FC } from "react";

type Props = {
  /**
   * invisible reCAPTCHA の描画先の id。要素は呼び出し側が用意する。
   * 表示が切り替わっても消えない位置に置くこと。
   */
  recaptchaContainerId: string;
  /** 何のために電話番号が要るかの説明。置かれる画面で変わるため呼び出し側が決める */
  description: string;
  /** 認証済みのときに番号の下に出す補足。省くと出さない */
  verifiedNote?: string;
  /** 紐付けが完了したとき。呼び出し側が購入可否などを更新するために使う */
  onVerified?: () => void;
};

/**
 * 電話番号の認証状態を表すカード。
 *
 * 未認証なら登録フォーム、認証済みなら番号と変更導線、変更中なら差し替えの
 * フォームを出す。チケット選択画面とアカウント編集画面で同じものを使う。
 */
export const PhoneNumberVerification: FC<Props> = ({
  recaptchaContainerId,
  description,
  verifiedNote,
  onVerified,
}) => {
  const { t } = useI18n();
  const classes = styles();

  // 認証直後は firebase.User が同一参照のまま更新されるため、
  // store ではなく currentUser を見る。再描画のきっかけは verifiedAt で作る
  const [verifiedAt, setVerifiedAt] = useState(0);
  const [isChanging, setIsChanging] = useState(false);
  const registeredPhoneNumber =
    firebase.auth().currentUser?.phoneNumber ?? null;

  if (registeredPhoneNumber !== null && !isChanging) {
    return (
      <div css={classes.card} key={verifiedAt}>
        <div css={classes.top}>
          <div css={classes.left}>
            <p css={classes.status}>
              <CheckCircleIcon css={classes.statusIcon} />
              <span>{t("phoneNumber.statusVerified")}</span>
            </p>
            <div css={classes.registered}>
              <p css={classes.registeredLabel}>{t("phoneNumber.registered")}</p>
              <p css={classes.registeredNumber}>
                {formatPhoneNumber(registeredPhoneNumber)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsChanging(true)}
            css={classes.changeButton}
          >
            {t("phoneNumber.changeNumber")}
          </button>
        </div>
        {verifiedNote !== undefined && <p css={classes.note}>{verifiedNote}</p>}
      </div>
    );
  }

  return (
    <PhoneNumberVerificationForm
      recaptchaContainerId={recaptchaContainerId}
      description={description}
      currentPhoneNumber={registeredPhoneNumber ?? undefined}
      onCancel={() => setIsChanging(false)}
      onVerified={() => {
        setIsChanging(false);
        setVerifiedAt(Date.now());
        onVerified?.();
      }}
    />
  );
};

const styles = () => {
  return {
    card: css({
      display: "flex",
      flexDirection: "column",
      gap: "20px",
      width: "100%",
      maxWidth: "740px",
      padding: "24px",
      backgroundColor: "#eaf7f5",
      border: "2px solid #57bfae",
    }),
    top: css({
      display: "flex",
      flexWrap: "wrap",
      gap: "16px",
      alignItems: "center",
      justifyContent: "space-between",
    }),
    left: css({
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    }),
    status: css({
      display: "flex",
      gap: "10px",
      alignItems: "center",
      margin: 0,
      fontSize: "18px",
      fontWeight: "bold",
      lineHeight: "25px",
    }),
    statusIcon: css({
      flex: "none",
      fontSize: "24px",
      color: "#00c2ae",
    }),
    registered: css({
      display: "flex",
      flexDirection: "column",
      gap: "2px",
    }),
    registeredLabel: css({
      margin: 0,
      color: "#6f6f6f",
      fontSize: "13px",
      lineHeight: "20px",
    }),
    registeredNumber: css({
      margin: 0,
      fontSize: "22px",
      fontWeight: "bold",
      lineHeight: "30px",
    }),
    changeButton: css({
      flex: "none",
      width: "180px",
      padding: "10px 16px",
      color: "#0b0b0b",
      fontFamily: "inherit",
      fontSize: "14px",
      fontWeight: "bold",
      lineHeight: "20px",
      backgroundColor: "#fff",
      border: "1px solid #0b0b0b",
      cursor: "pointer",
    }),
    note: css({
      margin: 0,
      color: "#6f6f6f",
      fontSize: "13px",
      lineHeight: "20px",
    }),
  };
};
